import { FC, useState, useCallback, useMemo, useEffect, LegacyRef, SyntheticEvent } from 'react';
import { Link } from 'routes';
import classnames from 'classnames';
import { captureException } from '@sentry/nextjs';

import { checkIsDesktop } from 'utils/layout';
import { useWindowSize, useGetProJobCategoriesTree } from 'src/hooks';
import { useIsVisible } from '@z-components/hooks';

import Expand from 'images/icons/Icon_Expand_More.svg';
import Collapse from 'images/icons/Icon_Up.svg';

import type {
  ProJobCategoriesForSignup as ProJobCategoriesForSignUp,
  ProJobCategoryTree,
} from '../../../marketing-pages/marketing-page.types';

import styles from './ProSignUpTree.module.scss';
import { logEvent } from 'utils/analytics';

export const JobTypeNode: FC<{
  depth: number;
  displayName: string;
  jobTypeId: string;
  track?: (jobTypeId: string) => void;
}> = ({ jobTypeId, displayName, track }) => {
  const route = `/sign-up/pro?jobTypeId=${jobTypeId}`;

  const trackHandler = useCallback(() => {
    if (track) track(jobTypeId);
    else logEvent('proSignUp', 'startedRegistration', `ProSignupTrees_${jobTypeId}`);
  }, [track, jobTypeId]);

  return (
    <Link route={route} passHref={true}>
      <a className={styles['job-type-node']} onClick={trackHandler} role="link" tabIndex={0}>
        <span className={styles['node-header-job-type']}>{displayName}</span>
      </a>
    </Link>
  );
};

const DepthHeader: FC<{ classNames?: string; depth: number }> = ({
  depth,
  children,
  classNames,
}) => {
  const depthClassNames = classnames(styles['depth-header'], classNames);
  switch (depth) {
    case 0: {
      return <h3 className={depthClassNames}>{children}</h3>;
    }
    case 1: {
      return <h4 className={depthClassNames}>{children}</h4>;
    }
    default: {
      return <p className={depthClassNames}>{children}</p>;
    }
  }
};

const ExpandCollapseIconButton: FC<{
  isExpanded: boolean;
  onClick: (event: SyntheticEvent) => void;
}> = ({ isExpanded, onClick }) => (
  <button type="button" className={styles['collapse-expand-button']} onClick={onClick}>
    {isExpanded ? <Collapse /> : <Expand />}
  </button>
);

const CategoryNode: FC<{
  categoryName: string;
  depth: number;
  displayName: string;
  isImmediateLeafSpecialCase: boolean;
  isInitiallyExpanded?: boolean;
  isParentExpanded: boolean;
}> = ({
  displayName,
  depth,
  children,
  isParentExpanded,
  isImmediateLeafSpecialCase,
  isInitiallyExpanded,
}) => {
  const [isExpanded, setIsExpanded] = useState(Boolean(isInitiallyExpanded));
  const handleClick = useCallback((event: SyntheticEvent) => {
    // prevent the button from propagating the event so we dont immediately switch from
    // one state to the other (e.g. false -> true -> false when pressing <button/>)
    event.stopPropagation();
    setIsExpanded((isExpandedParam) => !isExpandedParam);
  }, []);
  return (
    <div
      className={classnames(styles['category-level'], {
        [styles['category-with-depth']]: depth > 0,
        [styles['category-level_expanded']]: isParentExpanded,
      })}
      onClick={handleClick}
      role="button"
      tabIndex={0}
    >
      <div className={styles['node-container']}>
        <DepthHeader classNames={styles['node-header']} depth={depth}>
          {displayName}
        </DepthHeader>
        <ExpandCollapseIconButton isExpanded={isExpanded} onClick={handleClick} />
      </div>
      {isExpanded && typeof children === 'function' ? (
        <div
          className={classnames(styles['category-level-root'], {
            [styles['immediate-leaf-style']]: isImmediateLeafSpecialCase,
          })}
        >
          {children(isExpanded)}
        </div>
      ) : null}
    </div>
  );
};

const Node: FC<{
  depth: number;
  isInitiallyExpanded?: boolean;
  isParentExpanded: boolean;
  nodeData: ProJobCategoriesForSignUp['trees'][0];
  track?: (jobTypeId: string) => void;
}> = ({
  children,
  nodeData: { jobTypeId, displayName, categoryName, children: nodeChildren },
  depth,
  isParentExpanded,
  isInitiallyExpanded,
  track,
}) => {
  const isImmediateLeafSpecialCase =
    depth === 0 && typeof nodeChildren?.[0]?.jobTypeId === 'string';
  if (typeof jobTypeId === 'string') {
    return (
      <JobTypeNode jobTypeId={jobTypeId} displayName={displayName} depth={depth} track={track}>
        {children}
      </JobTypeNode>
    );
  }
  return (
    <CategoryNode
      categoryName={categoryName as string}
      displayName={displayName}
      depth={depth}
      isParentExpanded={isParentExpanded}
      isImmediateLeafSpecialCase={isImmediateLeafSpecialCase}
      isInitiallyExpanded={isInitiallyExpanded}
    >
      {children}
    </CategoryNode>
  );
};

const Tree: FC<{
  depth: number;
  isInitiallyExpanded?: boolean;
  isParentExpanded?: boolean;
  nodeData: ProJobCategoriesForSignUp['trees'][0];
  track?: (jobTypeId: string) => void;
}> = ({ nodeData, depth, isParentExpanded = false, isInitiallyExpanded, track }) => {
  const { children: nodeChildren = [] } = nodeData;
  const isLevelInitiallyExpanded = isInitiallyExpanded && depth === 0;

  return (
    <Node
      depth={depth}
      nodeData={nodeData}
      isParentExpanded={isParentExpanded}
      isInitiallyExpanded={isLevelInitiallyExpanded}
      track={track}
    >
      {(isParentExpandedParam: boolean) =>
        nodeChildren.map((child) => {
          if (!child) {
            return null;
          }
          return (
            <Tree
              key={child.displayName}
              nodeData={child}
              depth={depth + 1}
              isParentExpanded={isParentExpandedParam}
              track={track}
            />
          );
        })
      }
    </Node>
  );
};

const TreeDesktopContainer: FC<{
  track?: (jobTypeId: string) => void;
  trees: Array<ProJobCategoryTree>;
}> = ({ trees, track }) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const [selectedTab, setSelectedTab] = useState<string>(trees[0]?.displayName ?? '');
  const treeRootsToChildren = trees.reduce<{ [displayName: string]: ProJobCategoryTree }>(
    (acc, cur) => {
      if (typeof cur.displayName === 'string' && cur.children) {
        acc[cur.displayName] = cur;
      }
      return acc;
    },
    {}
  );
  const handleClick = useCallback((option: string) => {
    setSelectedTab(option);
  }, []);
  return (
    <div className={styles['tree-root-desktop']}>
      <VerticalTabs
        options={Object.keys(treeRootsToChildren)}
        onClick={handleClick}
        selectedOption={selectedTab}
      />
      {selectedTab in treeRootsToChildren && (
        <MainDisplay tree={treeRootsToChildren[selectedTab]} track={track} />
      )}
    </div>
  );
};

const VerticalTabs: React.FC<{
  onClick: (option: string) => void;
  options: Array<string>;
  selectedOption: string;
}> = ({ options, onClick, selectedOption }) => (
  <div className={styles['vertical-tabs']}>
    {options.map((option) => (
      <div
        role="button"
        tabIndex={0}
        className={classnames(styles['tab'], { [styles['tab_active']]: selectedOption === option })}
        key={option}
        onClick={() => onClick(option)}
      >
        <span>{option}</span>
        <div className={classnames({ [styles['bootleg-triangle']]: selectedOption === option })} />
      </div>
    ))}
  </div>
);

const MainDisplay: FC<{
  track?: (jobTypeId: string) => void;
  tree: ProJobCategoryTree;
}> = ({ tree, track }) => {
  const length = tree.children?.length ?? 0;
  // round up so more items are in the left col than right
  const midpoint = Math.round(length / 2);
  const colOne = tree.children?.slice(0, midpoint) ?? [];
  const colTwo = tree.children?.slice(midpoint, length) ?? [];

  return (
    <div className={styles['main-display']}>
      {
        <div className={styles['col']}>
          {colOne.map(
            (child) =>
              child && <Tree key={child.displayName} nodeData={child} depth={1} track={track} />
          )}
        </div>
      }
      {
        <div className={styles['col']}>
          {colTwo.map(
            (child) =>
              child && <Tree key={child.displayName} nodeData={child} depth={1} track={track} />
          )}
        </div>
      }
    </div>
  );
};

const ProSignUpTree: FC<{
  data: ProJobCategoriesForSignUp;
  track?: (jobTypeId: string) => void;
}> = ({
  data,

  track,
}) => {
  const depth = 0;
  const { trees, createdDate } = data;
  const [{ data: clientSideData, loading, error }, manualRequest] = useGetProJobCategoriesTree(
    { createdDate },
    true
  );
  const treesToRender = clientSideData?.trees ?? trees;

  const { width: windowWidth } = useWindowSize();
  const { ref: proSignUpTreeRef, isVisible } = useIsVisible();
  const isDesktop = useMemo(() => checkIsDesktop(windowWidth), [windowWidth]);
  const RootComponent = isDesktop ? (
    <TreeDesktopContainer trees={treesToRender} track={track} />
  ) : (
    treesToRender.map((tree, index) => (
      <Tree
        key={tree.displayName}
        nodeData={tree}
        depth={depth}
        isInitiallyExpanded={index === 0}
        track={track}
      />
    ))
  );
  useEffect(() => {
    if (isVisible && !clientSideData && !loading && !error) {
      manualRequest().catch((error) => captureException(error));
    }
  }, [isVisible, clientSideData, loading, error, manualRequest]);
  return (
    <div
      ref={proSignUpTreeRef as LegacyRef<HTMLDivElement>}
      className={classnames({
        [styles['tree-root']]: !isDesktop,
        [styles['tree-root-desktop']]: isDesktop,
      })}
    >
      {RootComponent}
    </div>
  );
};

export default ProSignUpTree;
